import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import { useTimeLogs } from '../hooks/useTimeLogs';
import { useTodos } from '../hooks/useTodos';
import { useCategories } from '../hooks/useCategories';
import { Category, TimeLogEntry, ToDoItem } from '../types/api';
import EmptyState from '../components/EmptyState';
import TimeBarChart, { BarBucket, BarSeries } from '../components/TimeBarChart';
import { getApiErrorMessage } from '../utils/errors';
import { accentForId, OTHER_COLOR, UNCATEGORIZED_COLOR } from '../utils/colors';
import {
  Granularity,
  bucketFullLabelFor,
  bucketKeyFor,
  bucketLabelFor,
  defaultRangeFor,
  enumerateBuckets,
  formatDuration,
  parseDurationToSeconds,
  parseISODate,
} from '../utils/datetime';

type GroupBy = 'category' | 'task';

const TOP_N = 8; // distinct series before the long tail rolls into "Other"
const MAX_BUCKETS = 90;

const INTERVALS: { value: Granularity; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

interface ChartData {
  series: BarSeries[];
  buckets: BarBucket[];
  totalSeconds: number;
  busiest: { label: string; seconds: number } | null;
  truncated: boolean;
  hasData: boolean;
}

/** Aggregate raw time logs into stacked-bar series + buckets. Pure + memoizable. */
function buildChartData(
  logs: TimeLogEntry[],
  categories: Category[],
  todos: ToDoItem[],
  fromISO: string,
  toISO: string,
  granularity: Granularity,
  groupBy: GroupBy
): ChartData {
  const lo = fromISO <= toISO ? fromISO : toISO;
  const hi = fromISO <= toISO ? toISO : fromISO;

  const bucketKeys = enumerateBuckets(parseISODate(lo), parseISODate(hi), granularity, MAX_BUCKETS);
  const bucketSet = new Set(bucketKeys);
  const truncated = bucketKeys.length > 0 && bucketKeys[0] !== bucketKeyFor(parseISODate(lo), granularity);

  const catName = new Map<number, string>();
  categories.forEach((c) => catName.set(c.id, c.name));
  const todoTitle = new Map<number, string>();
  todos.forEach((t) => todoTitle.set(t.id, t.title));

  interface Acc {
    label: string;
    color: string;
    total: number;
    perBucket: Map<string, number>;
  }
  const accs = new Map<string, Acc>();
  const neutralKey = groupBy === 'category' ? '__uncategorized__' : '__unlinked__';
  let totalSeconds = 0;

  for (const log of logs) {
    if (log.entry_date < lo || log.entry_date > hi) continue;
    const bucketKey = bucketKeyFor(parseISODate(log.entry_date), granularity);
    if (!bucketSet.has(bucketKey)) continue;
    const secs = parseDurationToSeconds(log.time_logged);
    if (secs <= 0) continue;
    totalSeconds += secs;

    let key: string;
    let label: string;
    let color: string;
    if (groupBy === 'category') {
      if (log.category != null) {
        key = `c${log.category}`;
        label = catName.get(log.category) ?? log.category_name ?? `Category ${log.category}`;
        color = accentForId(log.category);
      } else {
        key = neutralKey;
        label = 'Uncategorized';
        color = UNCATEGORIZED_COLOR;
      }
    } else if (log.belongs_to_todo != null) {
      key = `t${log.belongs_to_todo}`;
      label = todoTitle.get(log.belongs_to_todo) ?? log.belongs_to_todo_title ?? `Task ${log.belongs_to_todo}`;
      color = accentForId(log.belongs_to_todo);
    } else {
      key = neutralKey;
      label = 'Unlinked';
      color = UNCATEGORIZED_COLOR;
    }

    let acc = accs.get(key);
    if (!acc) {
      acc = { label, color, total: 0, perBucket: new Map() };
      accs.set(key, acc);
    }
    acc.total += secs;
    acc.perBucket.set(bucketKey, (acc.perBucket.get(bucketKey) ?? 0) + secs);
  }

  // Rank the real series; keep the top N, roll the rest into "Other". The
  // neutral bucket (Uncategorized / Unlinked) is always kept and shown last.
  const neutral = accs.get(neutralKey);
  const realSorted = Array.from(accs.entries())
    .filter(([k]) => k !== neutralKey)
    .sort((a, b) => b[1].total - a[1].total);

  const finalOf = new Map<string, string>();
  realSorted.forEach(([k], i) => finalOf.set(k, i < TOP_N ? k : '__other__'));
  if (neutral) finalOf.set(neutralKey, neutralKey);

  const presentation = new Map<string, { label: string; color: string }>();
  realSorted.slice(0, TOP_N).forEach(([k, a]) => presentation.set(k, { label: a.label, color: a.color }));
  if (realSorted.length > TOP_N) {
    presentation.set('__other__', { label: `Other (${realSorted.length - TOP_N})`, color: OTHER_COLOR });
  }
  if (neutral) presentation.set(neutralKey, { label: neutral.label, color: neutral.color });

  const finalTotals = new Map<string, number>();
  const finalPerBucket = new Map<string, Map<string, number>>();
  accs.forEach((acc, k) => {
    const fk = finalOf.get(k) as string;
    finalTotals.set(fk, (finalTotals.get(fk) ?? 0) + acc.total);
    let pb = finalPerBucket.get(fk);
    if (!pb) {
      pb = new Map();
      finalPerBucket.set(fk, pb);
    }
    acc.perBucket.forEach((v, bk) => pb!.set(bk, (pb!.get(bk) ?? 0) + v));
  });

  const orderedKeys: string[] = [];
  realSorted.slice(0, TOP_N).forEach(([k]) => orderedKeys.push(k));
  if (realSorted.length > TOP_N) orderedKeys.push('__other__');
  if (neutral) orderedKeys.push(neutralKey);

  const series: BarSeries[] = orderedKeys.map((k) => {
    const p = presentation.get(k) as { label: string; color: string };
    return { key: k, label: p.label, color: p.color, total: finalTotals.get(k) ?? 0 };
  });

  let busiestLabel = '';
  let busiestSecs = -1;
  const buckets: BarBucket[] = bucketKeys.map((bk) => {
    const values: Record<string, number> = {};
    let total = 0;
    series.forEach((s) => {
      const v = finalPerBucket.get(s.key)?.get(bk) ?? 0;
      if (v > 0) {
        values[s.key] = v;
        total += v;
      }
    });
    if (total > busiestSecs) {
      busiestSecs = total;
      busiestLabel = bucketFullLabelFor(bk, granularity);
    }
    return {
      key: bk,
      label: bucketLabelFor(bk, granularity),
      fullLabel: bucketFullLabelFor(bk, granularity),
      values,
      total,
    };
  });

  return {
    series,
    buckets,
    totalSeconds,
    busiest: busiestSecs > 0 ? { label: busiestLabel, seconds: busiestSecs } : null,
    truncated,
    hasData: series.length > 0 && totalSeconds > 0,
  };
}

const StatTile: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Card>
    <CardContent>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: logs, isLoading, isError, error, refetch } = useTimeLogs();
  const { data: categories } = useCategories();
  const { data: todos } = useTodos();

  const [granularity, setGranularity] = useState<Granularity>('day');
  const [groupBy, setGroupBy] = useState<GroupBy>('category');
  const [range, setRange] = useState(() => defaultRangeFor('day'));

  const changeGranularity = (g: Granularity | null) => {
    if (!g) return;
    setGranularity(g);
    setRange(defaultRangeFor(g)); // reset to a sensible window for the new interval
  };

  const chart = useMemo(
    () => buildChartData(logs ?? [], categories ?? [], todos ?? [], range.from, range.to, granularity, groupBy),
    [logs, categories, todos, range, granularity, groupBy]
  );

  const avgSeconds = chart.buckets.length > 0 ? chart.totalSeconds / chart.buckets.length : 0;
  const intervalNoun = granularity;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Analytics</Typography>
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? 'Loading…'
            : `${formatDuration(chart.totalSeconds)} logged across ${chart.buckets.length} ${intervalNoun}${chart.buckets.length === 1 ? '' : 's'}`}
        </Typography>
      </Box>

      {isError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {getApiErrorMessage(error, 'Could not load your time logs.')}
        </Alert>
      ) : isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={88} sx={{ borderRadius: 3.5 }} />
          <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3.5 }} />
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {/* Controls */}
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2.5}
                alignItems={{ xs: 'stretch', md: 'flex-end' }}
                flexWrap="wrap"
                useFlexGap
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Interval
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={granularity}
                    onChange={(_, v) => changeGranularity(v)}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    {INTERVALS.map((i) => (
                      <ToggleButton key={i.value} value={i.value} sx={{ px: 2 }}>
                        {i.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Split by
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={groupBy}
                    onChange={(_, v) => v && setGroupBy(v)}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <ToggleButton value="category" sx={{ px: 2 }}>Category</ToggleButton>
                    <ToggleButton value="task" sx={{ px: 2 }}>Task</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={range.from}
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: range.to }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={range.to}
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: range.from }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Summary tiles */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <StatTile label="Total logged" value={formatDuration(chart.totalSeconds)} hint={`${groupBy === 'category' ? 'By category' : 'By task'}`} />
            <StatTile label={`Average per ${intervalNoun}`} value={formatDuration(avgSeconds)} hint={`Over ${chart.buckets.length} ${intervalNoun}${chart.buckets.length === 1 ? '' : 's'}`} />
            <StatTile
              label={`Busiest ${intervalNoun}`}
              value={chart.busiest ? formatDuration(chart.busiest.seconds) : '—'}
              hint={chart.busiest ? chart.busiest.label : 'No time logged in range'}
            />
          </Box>

          {chart.truncated && (
            <Alert severity="info">
              The selected range is large — showing the most recent {MAX_BUCKETS} {intervalNoun}s. Narrow the
              date range or choose a coarser interval to see the full span.
            </Alert>
          )}

          {/* Chart */}
          <Card>
            <CardContent sx={{ pt: 3 }}>
              {chart.hasData ? (
                <TimeBarChart buckets={chart.buckets} series={chart.series} />
              ) : (
                <EmptyState
                  icon={<BarChartRoundedIcon />}
                  title="No time logged in this range"
                  description="Try a different date range or interval — or log some time to see it broken down here."
                  actionLabel="Go to Time Logs"
                  onAction={() => navigate('/time-logs')}
                />
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default AnalyticsPage;
